// __root.tsx — R-01, R-07
// Route racine TanStack Router. Contient les providers globaux et le guard d'authentification.
// Dual-auth (004): détecte JWT local AVANT les templates MSAL pour permettre l'auth email/password.

import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import {
  createRootRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { lazy, Suspense, useSyncExternalStore } from "react";

import { AuthProvider, useAuth } from "@/application/contexts/AuthContext";
import { MockAuthProvider } from "@/application/contexts/MockAuthProvider";
import { UIProvider } from "@/application/contexts/UIContext";
import { toast } from "@/application/hooks/use-toast";
import { reportClientError } from "@/application/utils/errorReporter";
import { appConfig } from "@/config";
import {
  AUTH_METHOD_CHANGED_EVENT,
  AUTH_METHOD_KEY,
  LOCAL_AUTH_TOKEN_KEY,
} from "@/domain/constants/auth-constants";
import { ApiError } from "@/infrastructure/adapters/problem-details";
import { getMsalInstance } from "@/infrastructure/msal/auth-helpers";
import { ErrorBoundary } from "@/presentation/components/shared/ErrorBoundary";
import { PageErrorFallback } from "@/presentation/components/shared/PageErrorFallback";
import { Toaster } from "@/presentation/components/shared/ui/toaster";
import { LoadingSpinner } from "@/shared/components/LoadingSpinner";

const IS_MOCK_AUTH = appConfig.devMockAuth;
const LazyLoginPage = lazy(() =>
  import("@/presentation/pages/LoginPage").then(({ LoginPage }) => ({
    default: LoginPage,
  })),
);

function reportUnexpectedApiError(
  error: unknown,
  operation: string,
  key?: readonly unknown[],
): void {
  if (error instanceof ApiError && error.status > 0 && error.status < 500) {
    return;
  }

  reportClientError("backoffice.api", error, {
    operation,
    queryKey: key ? JSON.stringify(key) : "",
    ...(error instanceof ApiError
      ? {
          apiStatus: String(error.status),
          apiDetail: error.detail ?? "",
        }
      : {}),
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportUnexpectedApiError(error, "query", query.queryKey);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _onMutateResult, mutation) => {
      reportUnexpectedApiError(error, "mutation", mutation.options.mutationKey);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 secondes — constitution §IV
      refetchOnWindowFocus: appConfig.env !== "development",
      retry: 1,
    },
    mutations: {
      onError: (error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : "Une erreur inattendue s'est produite.";
        toast({
          variant: "destructive",
          title: "Erreur",
          description: message,
        });
      },
    },
  },
});

const msalInstance = IS_MOCK_AUTH ? null : getMsalInstance();

function isPublicAuthRoute(pathname: string): boolean {
  return pathname === "/reset-password" || pathname === "/accept-invite";
}

function subscribeToLocalAuthToken(onStoreChange: () => void): () => void {
  if (!("window" in globalThis)) return () => undefined;

  const handleChange = () => onStoreChange();
  globalThis.window.addEventListener("storage", handleChange);
  globalThis.window.addEventListener("local-auth-token-changed", handleChange);
  globalThis.window.addEventListener(AUTH_METHOD_CHANGED_EVENT, handleChange);

  return () => {
    globalThis.window.removeEventListener("storage", handleChange);
    globalThis.window.removeEventListener(
      "local-auth-token-changed",
      handleChange,
    );
    globalThis.window.removeEventListener(
      AUTH_METHOD_CHANGED_EVENT,
      handleChange,
    );
  };
}

function getLocalAuthRouteSnapshot(): boolean {
  if (!("window" in globalThis)) return false;
  const selectedAuthMethod =
    globalThis.window.sessionStorage.getItem(AUTH_METHOD_KEY);
  return (
    selectedAuthMethod === "email" ||
    (selectedAuthMethod !== "microsoft" &&
      !!globalThis.window.sessionStorage.getItem(LOCAL_AUTH_TOKEN_KEY))
  );
}

function getLocalTokenSnapshot(): boolean {
  if (!("window" in globalThis)) return false;
  return !!globalThis.window.sessionStorage.getItem(LOCAL_AUTH_TOKEN_KEY);
}

function PendingRoleAssignmentState({
  onLogout,
}: {
  readonly onLogout: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <Clock className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">
            Attribution du rôle en attente
          </h1>
          <p className="text-sm text-muted-foreground">
            Votre compte est en attente d&apos;attribution d&apos;un rôle.
            Contactez un administrateur pour obtenir l&apos;accès à
            l&apos;application.
          </p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

// Guard interne — affiché après que MsalProvider et AuthProvider soient initialisés
function AuthGuard() {
  const { isLoading, error, errorStatus, currentUser, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Chargement du profil…" />
      </div>
    );
  }

  if (error) {
    const displayStatus = errorStatus ?? 500;
    return (
      <div className="flex h-screen items-center justify-center">
        <PageErrorFallback
          status={displayStatus}
          message={error}
          onRetry={
            displayStatus === 0 || displayStatus >= 500
              ? () => globalThis.location.reload()
              : undefined
          }
        />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Chargement du profil…" />
      </div>
    );
  }

  if (currentUser.isPendingRoleAssignment) {
    return <PendingRoleAssignmentState onLogout={() => void logout()} />;
  }

  return <Outlet />;
}

export const Route = createRootRoute({
  component: function RootComponent() {
    const pathname = useRouterState({
      select: (state) => state.location.pathname,
    });

    // R-07 : local auth selected → keep MSAL templates from taking over
    const hasLocalAuth = useSyncExternalStore(
      subscribeToLocalAuthToken,
      getLocalAuthRouteSnapshot,
      () => false,
    );
    // Must be subscribed: on "/" navigating to "/" after MFA does not re-render this component.
    const hasLocalToken = useSyncExternalStore(
      subscribeToLocalAuthToken,
      getLocalTokenSnapshot,
      () => false,
    );
    const publicRouteRequested = isPublicAuthRoute(pathname);

    const inner = (
      <UIProvider>
        <ErrorBoundary>
          <AuthGuard />
        </ErrorBoundary>
        <Toaster />
      </UIProvider>
    );

    if (IS_MOCK_AUTH) {
      return (
        <QueryClientProvider client={queryClient}>
          {publicRouteRequested ? (
            <Outlet />
          ) : (
            <MockAuthProvider>{inner}</MockAuthProvider>
          )}
        </QueryClientProvider>
      );
    }

    let mainContent = null;
    if (publicRouteRequested) {
      mainContent = <Outlet />;
    } else if (hasLocalAuth) {
      mainContent = (
        <AuthProvider>
          {hasLocalToken ? (
            inner
          ) : (
            <Suspense
              fallback={
                <div className="flex h-screen items-center justify-center">
                  <LoadingSpinner
                    size="lg"
                    label="Chargement de la connexion…"
                  />
                </div>
              }
            >
              <LazyLoginPage />
            </Suspense>
          )}
        </AuthProvider>
      );
    } else {
      mainContent = (
        <AuthProvider>
          <UnauthenticatedTemplate>
            <Suspense
              fallback={
                <div className="flex h-screen items-center justify-center">
                  <LoadingSpinner
                    size="lg"
                    label="Chargement de la connexion…"
                  />
                </div>
              }
            >
              <LazyLoginPage />
            </Suspense>
          </UnauthenticatedTemplate>
          <AuthenticatedTemplate>{inner}</AuthenticatedTemplate>
        </AuthProvider>
      );
    }

    return (
      <MsalProvider instance={msalInstance!}>
        <QueryClientProvider client={queryClient}>
          {mainContent}
        </QueryClientProvider>
      </MsalProvider>
    );
  },
});
